<template>
  <div class="p-4 max-w-screen-xl w-full mx-auto">
    <!-- Skeleton Loading State -->
    <template v-if="isLoading">
      <!-- Header Skeleton -->
      <div class="flex justify-between items-start mb-4">
        <!-- Left Column Skeleton -->
        <div class="flex flex-col gap-2">
          <!-- Title skeleton -->
          <div class="flex items-center gap-2">
            <div class="w-5 h-5 bg-gray-200 rounded animate-pulse" />
            <div class="h-6 w-48 bg-gray-200 rounded animate-pulse" />
          </div>
          <!-- Used by skeleton -->
          <div class="flex items-center gap-2 mt-1">
            <div class="w-4 h-4 bg-amber-100 rounded animate-pulse" />
            <div class="h-4 w-16 bg-amber-100 rounded animate-pulse" />
            <div class="h-4 w-32 bg-amber-100 rounded animate-pulse" />
          </div>
        </div>

        <!-- Right Column Skeleton -->
        <div class="flex items-center gap-3">
          <!-- Week dropdown skeleton -->
          <div class="w-[180px] h-10 bg-gray-200 rounded-md animate-pulse" />
          <!-- Add button skeleton -->
          <div class="w-32 h-9 bg-gray-200 rounded-md animate-pulse" />
        </div>
      </div>

      <!-- Content Skeleton - Week Cards -->
      <CurriculumSkeleton :week-count="3" />
    </template>

    <!-- Actual Content -->
    <template v-else>
      <div class="flex justify-between items-start mb-4">
        <!-- Left Column: Back Link & Title -->
        <div class="flex flex-col">
          <div v-if="expeditionsStore?.expeditionData" class="flex flex-col items-start">
            <h1 class="text-xl font-normal cursor-default flex items-center">
              <BookOpenText class="w-5 h-5 mr-1" />
              {{ curriculumStore?.expeditionSteps?.path_name }}
            </h1>
            <div class="text-sm flex items-center text-amber-600 gap-1 mt-1">
              <CircleAlert class="w-4 h-4" />
              Used by:
              <span>
                {{ curriculumStore?.expeditionSteps?.expeditions.join(', ') }}
              </span>
            </div>
          </div>
        </div>

        <!-- Right Column: Add Path Step -->
        <div class="flex flex-col items-end">
          <div class="flex items-center gap-3">
            <!-- Week Navigation -->
            <Select v-model="selectedWeek" @update:model-value="scrollToWeek">
              <SelectTrigger class="w-[180px] bg-white">
                <SelectValue
                  :placeholder="selectedWeek ? `Week ${selectedWeek}` : 'Jump to week...'"
                />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectLabel>Navigate to Week</SelectLabel>
                  <SelectItem v-for="week in availableWeeks" :key="week" :value="week">
                    Week {{ week }}
                  </SelectItem>
                </SelectGroup>
              </SelectContent>
            </Select>

            <!-- Add Path Step Button -->
            <button
              v-if="isAdmin"
              class="text-xs text-white bg-cosmicPurple hover:bg-cosmicPurple/80 hover:text-white flex items-center rounded-md w-fit p-2 px-4 transition-all duration-200 border-[1px] border-gray-200"
              @click="openAddStepModal"
            >
              <Plus class="w-3 h-3 mr-2" />
              Add Path Step
            </button>
          </div>
        </div>
      </div>

      <!-- Steps Display -->
      <div v-if="curriculumStore.expeditionSteps?.path_steps">
        <ExpeditionSteps
          :path-steps-by-week="curriculumStore.expeditionSteps"
          :is-loading="curriculumStore.isStepsLoading"
          :hover-card-components="hoverCardComponents"
          :get-section-icon="getSectionIcon"
          :can-edit="isAdmin"
          @refresh-data="refreshExpeditionSteps"
          @update:visible-week="updateSelectedWeek"
        />
      </div>
    </template>

    <!-- Add Step Modal -->
    <AddStepModal
      v-if="showAddStepModal && isAdmin"
      :path-id="Number(expeditionId)"
      @close="closeAddStepModal"
      @created="onStepCreated"
    />
  </div>
</template>

<script setup lang="ts">
import { useCurriculumStore } from '@/stores/curriculum'
import { useRoute } from 'vue-router'
import { ref, computed, watch } from 'vue'
import ExpeditionSteps from '@/components/manage/ExpeditionSteps.vue'
import AddStepModal from '@/components/manage/AddStepModal.vue'
import CurriculumSkeleton from '@/components/manage/CurriculumSkeleton.vue'
import { BookOpenText, CircleAlert, Plus } from 'lucide-vue-next'
import { useExpeditionsStore } from '@/stores/expeditions'
import { getSectionIcon } from '@/const/curriculumSections'
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'
import { formatExpeditionName } from '@/utils/formatExpeditionName'
import { useSectionsStore } from '@/stores/sections'
import { useAuth } from '@/composables/useAuth'

// Create object with hover card components to pass down
const hoverCardComponents = {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger
}

const curriculumStore = useCurriculumStore()
const expeditionsStore = useExpeditionsStore()
const route = useRoute()
const sectionsStore = useSectionsStore()
const { isAdmin } = useAuth()

const expeditionId = computed(() => route.params.id as string)

// Loading state - true when either store is loading and we don't have data yet
const isLoading = computed(() => {
  return (
    (expeditionsStore?.isLoading || curriculumStore?.isStepsLoading) &&
    !curriculumStore?.expeditionSteps?.path_steps
  )
})

// Week navigation
const selectedWeek = ref<string>('')

// Modal state
const showAddStepModal = ref(false)

const updateSelectedWeek = (week: string) => {
  selectedWeek.value = week
}

const availableWeeks = computed(() => {
  if (!curriculumStore.expeditionSteps?.path_steps) return []
  return Object.keys(curriculumStore.expeditionSteps.path_steps)
    .map((week) => parseInt(week))
    .sort((a, b) => {
      // If both are negative or both are positive/zero, sort normally
      if ((a < 0 && b < 0) || (a >= 0 && b >= 0)) {
        return a - b
      }
      // If a is negative and b isn't, a should go last
      if (a < 0) return 1
      // If b is negative and a isn't, b should go last
      if (b < 0) return -1
      return 0
    })
    .map((week) => week.toString())
})

const scrollToWeek = () => {
  if (selectedWeek.value) {
    const targetElement = document.getElementById(`week-${selectedWeek.value}`)
    if (targetElement) {
      targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' })
      // Add a larger offset from the top (16rem = 256px)
      window.scrollBy(0, -256)
    }
  }
}

const loadCurriculumData = async () => {
  await curriculumStore.fetchExpeditionSteps(expeditionId.value)
  await expeditionsStore.fetchExpeditionData(expeditionId.value)
  await sectionsStore.fetchSections()
}

watch(expeditionId, () => {
  // eslint-disable-next-line @typescript-eslint/no-floating-promises
  loadCurriculumData()
}, { immediate: true })

// Watch for expedition data changes and update title
watch(
  () => expeditionsStore.expeditionData?.name,
  (newName) => {
    if (newName) {
      document.title = `${formatExpeditionName(newName)} Tasks | Outtalent Campus`
    }
  },
  { immediate: true }
)

// Refresh expedition steps after edits
const refreshExpeditionSteps = async () => {
  await curriculumStore.fetchExpeditionSteps(expeditionId.value)
}

// Modal handlers
const openAddStepModal = () => {
  showAddStepModal.value = true
}

const closeAddStepModal = () => {
  showAddStepModal.value = false
}

const onStepCreated = () => {
  // eslint-disable-next-line @typescript-eslint/no-floating-promises
  refreshExpeditionSteps()
}
</script>
